import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Flex, Button, Text, Spacer, List, ListItem, Spinner } from '@chakra-ui/react';
import Locale from '../../../utils/translations';
import { BonfireAPI, ApiType } from '../../../models/BonfireApi';
import config from '../../../utils/config';
import AddTagsModal from '../../common/AddTagsModal';
import { Template } from '../../../utils/interfaces';
import TemplateDetailsModal from './TemplateDetailsModal';

const TEMPLATE_FIELDS = [
	{ key: 'client', label: 'Client', defaultValue: 'local', isDisabled: true, isRequired: true },
	{
		key: 'segmentationType',
		label: 'Segmentation Type',
		defaultValue: 'default',
		isDisabled: false,
		isRequired: false,
	},
	{
		key: 'llmadapters',
		label: 'LLM Adapters',
		defaultValue: 'none',
		isDisabled: false,
		isRequired: false,
	},
	{
		key: 'createDummy',
		label: 'Create Dummy',
		defaultValue: 'false',
		isDisabled: false,
		isRequired: false,
	},
	{
		key: 'nocaptionsTimeout',
		label: 'No Captions Timeout',
		defaultValue: '2240000',
		isDisabled: false,
		isRequired: false,
	},
	{ key: 'mode', label: 'Mode', defaultValue: 'fast', isDisabled: false, isRequired: false },
	{
		key: 'translationProvider',
		label: 'Translation Provider',
		defaultValue: 'deepl',
		isDisabled: false,
		isRequired: false,
	},
	{
		key: 'speakingLanguage',
		label: 'Speaking Language',
		defaultValue: '',
		isDisabled: false,
		isRequired: true,
		disclaimer:
			'use format: <language code>:<provider>:<model>:<use llm 0, or 1>, e.g., en-US:deepgram:nova-3:0',
	},
	{
		key: 'useStream',
		label: 'useStream',
		defaultValue: 'true',
		isDisabled: false,
		isRequired: false,
	},
];

const TemplatesPage: React.FC = () => {
	const [templates, setTemplates] = useState<Template[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isCreating, setIsCreating] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState('');
	const [templateData, setTemplateData] = useState<{ [key: string]: string }>({});
	const [templateName, setTemplateName] = useState('');

	const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
	const [isDetailsOpen, setIsDetailsOpen] = useState(false);

	const defaultTemplateData = useMemo(
		() =>
			Object.fromEntries(TEMPLATE_FIELDS.map(field => [field.key, field.defaultValue || ''])),
		[],
	);

	const fetchTemplates = useCallback(async () => {
		setIsLoading(true);
		const url = `${config.qa_api.url}/aiModelComparisonsTemplates/list`;
		const api = new BonfireAPI(ApiType.QaApi);
		try {
			const response = await api.get(url);
			setTemplates(response);
		} catch (err) {
			console.error('Failed to fetch templates', err);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchTemplates();
		setTemplateData(defaultTemplateData);
	}, [fetchTemplates, defaultTemplateData]);

	const buildTags = useCallback((data: { [key: string]: string }) => {
		return Object.entries(data)
			.filter(([, value]) => value !== undefined && value !== '')
			.map(([key, value]) => ({ key, value }));
	}, []);

	const handleAddTemplate = useCallback(async () => {
		if (!templateName.trim()) {
			setError('Please enter a template name.');
			return;
		}

		const tags = buildTags(templateData);
		const url = `${config.qa_api.url}/aiModelComparisonsTemplates/add`;
		const api = new BonfireAPI(ApiType.QaApi);

		setIsCreating(true);
		setError('');
		try {
			await api.post(url, { name: templateName, tags });
			await fetchTemplates();
			setIsModalOpen(false);
			setTemplateData(defaultTemplateData);
			setTemplateName('');
		} catch (err: unknown) {
			if (err instanceof Error) {
				setError(err.message);
			} else {
				setError('Failed to add template');
			}
		} finally {
			setIsCreating(false);
		}
	}, [templateName, templateData, buildTags, fetchTemplates, defaultTemplateData]);

	const handleDeleteTemplate = useCallback(
		async (templateId: string) => {
			const url = `${config.qa_api.url}/aiModelComparisonsTemplates/delete/${templateId}`;
			const api = new BonfireAPI(ApiType.QaApi);
			try {
				await api.delete(url, {});
				await fetchTemplates();
			} catch (err) {
				console.error('Failed to delete template', err);
			}
		},
		[fetchTemplates],
	);

	return (
		<Box p={6}>
			<Flex mb={6} alignItems="center">
				<Text fontSize="2xl" fontWeight="bold">
					{Locale.get('Templates')}
				</Text>
				<Spacer />
				<Button variant="bePrimary" onClick={() => setIsModalOpen(true)}>
					{Locale.get('Add template')}
				</Button>
			</Flex>

			{isLoading ? (
				<Spinner />
			) : (
				<>
					<List spacing={3}>
						{templates.map(template => (
							<ListItem
								key={template.templateId || template.name}
								p={3}
								bg="white"
								borderRadius="md"
								boxShadow="sm"
								display="flex"
								alignItems="center"
								justifyContent="space-between"
							>
								<Text fontWeight="bold">{template.name}</Text>
								<Flex gap={2}>
									<Button
										size="sm"
										colorScheme="blue"
										variant="outline"
										onClick={() => {
											setSelectedTemplate(template);
											setIsDetailsOpen(true);
										}}
									>
										Details
									</Button>
									<Button
										size="sm"
										colorScheme="red"
										variant="outline"
										onClick={() => handleDeleteTemplate(template.templateId)}
									>
										Delete
									</Button>
								</Flex>
							</ListItem>
						))}
					</List>
					<TemplateDetailsModal
						isOpen={isDetailsOpen}
						onClose={() => setIsDetailsOpen(false)}
						template={selectedTemplate}
					/>
				</>
			)}

			<AddTagsModal
				section="templates"
				isOpen={isModalOpen}
				onClose={() => {
					setIsModalOpen(false);
					setTemplateData(defaultTemplateData);
				}}
				templateName={templateName}
				setTemplateName={setTemplateName}
				templateData={templateData}
				setTemplateData={setTemplateData}
				TEMPLATE_FIELDS={TEMPLATE_FIELDS}
				samples={[]} // not needed for templates
				selectedSampleId={''}
				setError={setError}
				setSelectedSampleId={() => {}}
				isCreating={isCreating}
				error={error}
				onAddTemplate={handleAddTemplate}
			/>
		</Box>
	);
};

export default TemplatesPage;
